import { useState } from 'react';

import { FormDoiTenVaiTro } from '../components/FormDoiTenVaiTro';
import { FormTaoVaiTro } from '../components/FormTaoVaiTro';
import { HopXoaVaiTro } from '../components/HopXoaVaiTro';
import { NganPhanQuyen } from '../components/NganPhanQuyen';
import type { Role } from '../types';
import { useDoiTenVaiTro, useGanQuyen, useTaoVaiTro, useXoaVaiTro } from './useVaiTro';

type HopTheoVaiTro = { mo: boolean; lan: number; vaiTro: Role | null };
const DONG: HopTheoVaiTro = { mo: false, lan: 0, vaiTro: null };

/**
 * Hộp thoại, ngăn và mutation của các thao tác trên vai trò — tạo, đổi tên,
 * phân quyền, xoá. Cùng khuôn `useThaoTacNguoiDung`: trả hàm mở từng việc và
 * `hopThoai` để view đặt vào cây.
 *
 * Mỗi lần mở tăng `lan`, dùng làm `key`: form dựng lại sạch mà không cần
 * effect đồng bộ.
 */
export function useThaoTacVaiTro() {
  const tao = useTaoVaiTro();
  const doiTen = useDoiTenVaiTro();
  const ganQuyen = useGanQuyen();
  const xoa = useXoaVaiTro();

  const [hopTao, setHopTao] = useState({ mo: false, lan: 0 });
  const [hopDoiTen, setHopDoiTen] = useState<HopTheoVaiTro>(DONG);
  const [nganQuyen, setNganQuyen] = useState<HopTheoVaiTro>(DONG);
  const [hopXoa, setHopXoa] = useState<{ mo: boolean; vaiTro: Role | null }>({ mo: false, vaiTro: null });

  const dongXoa = () => {
    setHopXoa((h) => ({ ...h, mo: false }));
    xoa.reset();
  };

  const vtDoiTen = hopDoiTen.vaiTro;
  const vtQuyen = nganQuyen.vaiTro;

  const hopThoai = (
    <>
      <FormTaoVaiTro
        key={`tao-${hopTao.lan}`}
        open={hopTao.mo}
        dangGui={tao.isPending}
        onClose={() => setHopTao((h) => ({ ...h, mo: false }))}
        onTao={(du) => tao.mutateAsync(du).then(() => setHopTao((h) => ({ ...h, mo: false })))}
      />
      {vtDoiTen && (
        <FormDoiTenVaiTro
          key={`ten-${hopDoiTen.lan}`}
          vaiTro={vtDoiTen}
          open={hopDoiTen.mo}
          dangLuu={doiTen.isPending}
          onClose={() => setHopDoiTen((h) => ({ ...h, mo: false }))}
          onLuu={(name) =>
            doiTen
              .mutateAsync({ id: vtDoiTen.id, name })
              .then(() => setHopDoiTen((h) => ({ ...h, mo: false })))
          }
        />
      )}
      {vtQuyen && (
        <NganPhanQuyen
          key={`quyen-${nganQuyen.lan}`}
          vaiTro={vtQuyen}
          open={nganQuyen.mo}
          dangLuu={ganQuyen.isPending}
          onClose={() => setNganQuyen((h) => ({ ...h, mo: false }))}
          onLuu={(permissions) =>
            ganQuyen
              .mutateAsync({ id: vtQuyen.id, permissions })
              .then(() => setNganQuyen((h) => ({ ...h, mo: false })))
          }
        />
      )}
      <HopXoaVaiTro
        open={hopXoa.mo}
        vaiTro={hopXoa.vaiTro}
        dangChay={xoa.isPending}
        loi={xoa.error}
        onClose={dongXoa}
        onXacNhan={(vt) => xoa.mutate(vt, { onSuccess: () => setHopXoa((h) => ({ ...h, mo: false })) })}
      />
    </>
  );

  return {
    moTao: () => setHopTao((h) => ({ mo: true, lan: h.lan + 1 })),
    moDoiTen: (vaiTro: Role) => setHopDoiTen((h) => ({ mo: true, lan: h.lan + 1, vaiTro })),
    moPhanQuyen: (vaiTro: Role) => setNganQuyen((h) => ({ mo: true, lan: h.lan + 1, vaiTro })),
    moXoa: (vaiTro: Role) => {
      // Lỗi của lần trước (có thể của vai trò khác) không được hiện ở hộp mới.
      xoa.reset();
      setHopXoa({ mo: true, vaiTro });
    },
    hopThoai,
  };
}
